<script setup lang="ts">
import {
    faChevronDown,
    faCircleInfo,
} from '@fortawesome/pro-regular-svg-icons';
import RouteInfo from '@components/util/trip/TripCardSegment/RouteInfo.vue';
import SegmentInfo from '@components/ui/hotelPage&reserve&view/segment/SegmentInfo.vue';
import type TripSegment from '@itinerary/trip/TripSegment';
import type TripPriceClass from '@itinerary/trip/TripPriceClass';

interface IProps {
    segment?: TripSegment;
    initialState?: 'opened' | 'closed';
    priceClass?: TripPriceClass;
}
const {
    segment = {} as TripSegment,
    initialState = 'opened',
    priceClass,
} = defineProps<IProps>();
const { isMobile } = useSize();
const isOpen = ref(true);
const toggle = () => {
    isOpen.value = !isOpen.value;
};
watch(
    () => initialState,
    val => {
        isOpen.value = val === 'opened';
    },
    { immediate: true }
);
onMounted(() => {
    if (isMobile.value) {
        isOpen.value = false;
    }
});

const destinationHasWarning = computed(
    () => segment.firstTrip?.destinationHasWarning
);
</script>

<template>
    <div class="flight-list-container">
        <div :class="{ open: isOpen }" class="flight-list">
            <div class="info-trips">
                <div
                    v-for="trip in segment.digest"
                    :key="trip.key"
                    class="info-trip"
                    @click="toggle"
                >
                    <div class="info-trip-details">
                        <div class="info-trip-route">
                            <div class="info-trip-route-point">
                                <mr-typography size="display4" weight="700">
                                    {{ trip.departureTime }}
                                </mr-typography>
                                <mr-typography color="var(--cool-gray-700)">
                                    {{ trip.originCityName }}
                                    <mr-typography
                                        v-if="
                                            trip.showCityCodes &&
                                            trip.originCode
                                        "
                                        type="span"
                                        size="small"
                                        color="var(--gray-600)"
                                    >
                                        ({{ trip.originCode }})
                                    </mr-typography>
                                </mr-typography>
                            </div>

                            <RouteInfo :segment class="flex" />
                            <div class="info-trip-route-point">
                                <mr-typography
                                    size="display4"
                                    weight="700"
                                    class="arrival-time"
                                >
                                    {{ trip.arrivalTime }}
                                    <mr-icon
                                        v-if="trip.destinationHasWarning"
                                        :icon="faCircleInfo"
                                        size="x-small"
                                        color="var(--cool-gray-600)"
                                    />
                                </mr-typography>
                                <mr-typography
                                    color="var(--cool-gray-700)"
                                    class="info-trip-route-city"
                                >
                                    <span class="arrival-time">
                                        {{ trip.destinationCityName }}
                                    </span>
                                    <mr-typography
                                        v-if="
                                            trip.showCityCodes &&
                                            trip.destinationCode
                                        "
                                        type="span"
                                        size="small"
                                        color="var(--gray-600)"
                                    >
                                        ({{ trip.destinationCode }})
                                    </mr-typography>
                                </mr-typography>
                            </div>
                        </div>
                        <mr-button
                            class="info-trip-toggle"
                            :icon-button="{
                                icon: faChevronDown,
                                color: 'black',
                            }"
                            variant="transparent"
                            size="small"
                        />
                    </div>
                </div>
            </div>
            <div class="info-timeline-container">
                <SegmentInfo
                    :segment
                    :compact="!destinationHasWarning"
                    :price-class
                />
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.info-trip-toggle {
    transform: matrix(-1, 0, 0, 1, 0, 0);
}

.open .info-trip-toggle {
    transform: rotate(180deg);
}

.flight-list-container {
    background: white;
    font-size: 17px;
    padding: 0;
    text-align: right;
    width: 100%;
}

.info-trip-details {
    margin-top: 8px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
}

.info-trip {
    position: relative;
    cursor: pointer;
}

.info-trips {
    padding: 8px 0;
}

.info-trip-route {
    white-space: nowrap;
    display: flex;
    align-items: flex-start;
    flex-grow: 1;
    .flex {
        display: flex !important;
    }
}

.info-trip-route > *:not(.clear) {
    display: inline-block;
}

.info-trip-route-city {
    display: flex;
    align-items: center;
    gap: 4px;
    .arrival-time {
        display: flex;
        align-items: center;
    }
}

.info-timeline-container {
    max-height: 0;
    transition: all 0.3s;
    overflow: hidden;
}

.open {
    .info-trips {
        border-bottom: 1px solid usePalette($p-cool-gray, $p-100);
    }
    .info-timeline-container {
        max-height: 1000px;
        padding: 16px 0;
        border-bottom: 1px solid usePalette($p-cool-gray, $p-100);
    }
}

.info-trip-route-point {
    width: 70px;
    .arrival-time {
        display: flex;
        align-items: center;
    }
}
</style>
